import React, { createContext, useState, useContext, useEffect } from 'react';

// Tipos para o questionário
export type BillingRange = '0 a 10k' | '10k a 100k' | '100k a 1M' | '1M a 10M' | '+10M';
export type Region = 'Brasil' | 'LATAM' | 'Global';
export type Platform = 'Hotmart' | 'PerfectPay' | 'Braip' | 'Kiwify' | 'Kirvano' | 'ClickBank' | 'Digistore' | 'Yampi' | 'Cartpanda' | 'Stripe' | 'Outros';

export interface QuizAnswer {
    billingVolume: BillingRange | null;
    region: Region | null;
    platform: Platform | null;
    integration: string;
    email: string;
    phone: string;
}

interface QuizContextType {
    answers: QuizAnswer;
    currentQuestion: number;
    updateAnswer: <K extends keyof QuizAnswer>(field: K, value: QuizAnswer[K]) => void;
    goToNextQuestion: () => void;
    goToPreviousQuestion: () => void;
    resetQuiz: () => void;
    isLastQuestion: boolean;
}

// Valor inicial para as respostas
const initialAnswers: QuizAnswer = {
    billingVolume: null,
    region: null,
    platform: null,
    integration: '',
    email: '',
    phone: '',
};

// Criar o contexto
const QuizContext = createContext<QuizContextType | undefined>(undefined);

// Hook para usar o contexto do quiz
export const useQuiz = (): QuizContextType => {
    const context = useContext(QuizContext);
    if (!context) {
        throw new Error('useQuiz deve ser usado dentro de um QuizProvider');
    }
    return context;
};

// Provider do quiz
export const QuizProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Estado para as respostas (com persistência no localStorage)
    const [answers, setAnswers] = useState<QuizAnswer>(() => {
        const savedAnswers = localStorage.getItem('quizAnswers');
        return savedAnswers ? JSON.parse(savedAnswers) : initialAnswers;
    });

    // Estado para a pergunta atual
    const [currentQuestion, setCurrentQuestion] = useState<number>(1);

    // Persistir respostas no localStorage quando mudam
    useEffect(() => {
        localStorage.setItem('quizAnswers', JSON.stringify(answers));
    }, [answers]);

    // Atualizar uma resposta específica
    const updateAnswer = <K extends keyof QuizAnswer>(field: K, value: QuizAnswer[K]) => {
        setAnswers(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Navegar para a próxima pergunta
    const goToNextQuestion = () => {
        if (currentQuestion < 6) {
            setCurrentQuestion(prev => prev + 1);
        }
    };

    // Navegar para a pergunta anterior
    const goToPreviousQuestion = () => {
        if (currentQuestion > 1) {
            setCurrentQuestion(prev => prev - 1);
        }
    };

    // Resetar o quiz
    const resetQuiz = () => {
        setAnswers(initialAnswers);
        setCurrentQuestion(1);
        localStorage.removeItem('quizAnswers');
    };

    // Verificar se é a última pergunta
    const isLastQuestion = currentQuestion === 6;

    return (
        <QuizContext.Provider
            value={{
                answers,
                currentQuestion,
                updateAnswer,
                goToNextQuestion,
                goToPreviousQuestion,
                resetQuiz,
                isLastQuestion
            }}
        >
            {children}
        </QuizContext.Provider>
    );
}; 